// packages/shared-auth/src/hooks/useSupabaseAuth.ts
import { useState, useEffect } from "react";
import { getSupabaseClient } from "@kordbooks/supabase-client";
export function useSupabaseAuth() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const supabase = getSupabaseClient();
    useEffect(() => {
        let isMounted = true;
        // Initialize from current session to reduce UI flicker
        supabase.auth.getSession().then(({ data }) => {
            if (!isMounted)
                return;
            const session = data.session;
            if (session?.user) {
                setUser({
                    id: session.user.id,
                    email: session.user.email ?? "",
                    role: session.user.user_metadata?.role ?? "client",
                    userlanguage: session.user.user_metadata?.userlanguage ?? "en",
                });
            }
            else {
                setUser(null);
            }
            setLoading(false);
        });
        // Subscribe to auth state changes (v2 shape)
        const { data: { subscription }, } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!isMounted)
                return;
            if (session?.user) {
                setUser({
                    id: session.user.id,
                    email: session.user.email ?? "",
                    role: session.user.user_metadata?.role ?? "client",
                    userlanguage: session.user.user_metadata?.userlanguage ?? "en",
                });
            }
            else {
                setUser(null);
            }
            setLoading(false);
        });
        // Proper cleanup for v2
        return () => {
            isMounted = false;
            subscription.unsubscribe();
        };
    }, [supabase]);
    const signIn = async (credentials) => {
        try {
            setError(null);
            setLoading(true);
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: credentials.email,
                password: credentials.password,
            });
            if (signInError)
                throw signInError;
        }
        catch (err) {
            const authError = err;
            setError({
                message: authError.message || "Sign in failed",
                code: authError.code,
            });
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const signUp = async (credentials) => {
        try {
            setError(null);
            setLoading(true);
            if (credentials.password !== credentials.confirmPassword) {
                throw new Error("Passwords do not match");
            }
            const { error: signUpError } = await supabase.auth.signUp({
                email: credentials.email,
                password: credentials.password,
                options: {
                    data: { role: "client", userlanguage: "en" },
                },
            });
            if (signUpError)
                throw signUpError;
        }
        catch (err) {
            const authError = err;
            setError({
                message: authError.message || "Sign up failed",
                code: authError.code,
            });
            throw err;
        }
        finally {
            setLoading(false);
        }
    };
    const signOut = async () => {
        try {
            setError(null);
            const { error: signOutError } = await supabase.auth.signOut();
            if (signOutError)
                throw signOutError;
            setUser(null);
        }
        catch (err) {
            const authError = err;
            setError({
                message: authError.message || "Sign out failed",
                code: authError.code,
            });
            throw err;
        }
    };
    const resetPassword = async (email) => {
        try {
            setError(null);
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth/reset-password`,
            });
            if (resetError)
                throw resetError;
        }
        catch (err) {
            const authError = err;
            setError({
                message: authError.message || "Password reset failed",
                code: authError.code,
            });
            throw err;
        }
    };
    // --- MFA helpers ---
    const listFactors = async () => {
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error)
            throw error;
        return data.totp; // matches how AccountSettings expects an array of TOTP factors
    };
    const enrollMFA = async () => {
        const { data, error } = await supabase.auth.mfa.enroll({
            factorType: "totp",
            // you can optionally add friendlyName here
            // friendlyName: "Authenticator app",
        });
        if (error)
            throw error;
        // data.totp.qr_code / secret / uri are used in the UI
        return data;
    };
    const challengeAndVerifyMFA = async (factorId, code) => {
        // Helper that does mfa.challenge() + mfa.verify() in one step [web:2][web:7]
        const { data, error } = await supabase.auth.mfa.challengeAndVerify({
            factorId,
            code,
        });
        if (error)
            throw error;
        return data;
    };
    const unenrollMFA = async (factorId) => {
        const { error } = await supabase.auth.mfa.unenroll({ factorId });
        if (error)
            throw error;
    };
    return {
        user,
        loading,
        error,
        signIn,
        signUp,
        signOut,
        resetPassword,
        // MFA helpers used by AccountSettings
        listFactors,
        enrollMFA,
        challengeAndVerifyMFA,
        unenrollMFA,
    };
}
//# sourceMappingURL=useSupabaseAuth.js.map